import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FiBell,
  FiBriefcase,
  FiEye,
  FiHeart,
  FiMessageSquare,
  FiStar,
  FiUserCheck,
} from "react-icons/fi";
import { useNotifications } from "../hooks/useNotifications";
import { useSuperApp } from "../hooks/useSuperApp";
import { useTimeAgo } from "../utils/getTimeAgo";
import { getApplicantStatus } from "../data/applicantStatus";

/**
 * The notification list itself — the rows, their wording, and where each one goes.
 *
 * Rendered by two things: the /notifications page (mobile, and anywhere a full page is
 * wanted) and the header's desktop panel. Both show the same list, so it lives here rather
 * than in either of them; what differs is only the shell around it.
 *
 * The rows are assembled on the client (see api/services/notifications.js). Wording lives
 * here rather than in the service so one place decides the language and the service stays a
 * pure fetcher.
 */

/** One icon per kind, so a glance down the list separates "someone looked at me" from "I moved". */
const ICONS = {
  stage: FiBriefcase,
  status: FiBriefcase,
  cvView: FiEye,
  follow: FiEye,
  comment: FiMessageSquare,
  reply: FiMessageSquare,
  like: FiHeart,
  suggest: FiStar,
  // No photoUrl carries this row (see notifications.js) — the briefcase is the whole
  // mark, so it has to read as "an offer" on its own rather than filling in for a photo.
  jobOffer: FiBriefcase,
  applicant: FiUserCheck,
  applicantStatus: FiUserCheck,
};

/** The accent behind the icon. Muted on purpose — the unread mark is the loud thing. */
const TONES = {
  stage: "#EFF6FF",
  status: "#EFF6FF",
  cvView: "#F1F5F9",
  follow: "#F1F5F9",
  comment: "#FEF9C3",
  reply: "#FEF9C3",
  like: "#FEE2E2",
  suggest: "#FEF9C3",
  jobOffer: "#DCFCE7",
  applicant: "#DCFCE7",
  applicantStatus: "#DCFCE7",
};

const DAY = 24 * 60 * 60 * 1000;

/**
 * Today / Yesterday / This week / Earlier.
 *
 * By calendar day rather than elapsed hours: something at 23:00 last night is "өчигдөр"
 * to the reader even though it is nine hours ago.
 */
const bucketOf = (iso) => {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "earlier";
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const diff = startOfToday.getTime() - then.getTime();
  if (diff <= 0) return "today";
  if (diff <= DAY) return "yesterday";
  if (diff <= 6 * DAY) return "thisWeek";
  return "earlier";
};

const BUCKET_ORDER = ["today", "yesterday", "thisWeek", "earlier"];

const RowSkeleton = () => (
  <div className="flex items-center gap-3 px-4 py-4 animate-pulse">
    <div className="w-10 h-10 rounded-full bg-[#E2E8F0] flex-none" />
    <div className="flex-1">
      <div className="h-3 bg-[#E2E8F0] rounded w-3/4" />
      <div className="h-2.5 bg-[#EEF1F8] rounded w-1/3 mt-2" />
    </div>
  </div>
);

/**
 * @param {() => void} [onNavigate] - called just before a row navigates. The panel closes
 *   itself with this; the page has nothing to close and leaves it off.
 */
export const NotificationList = ({ onNavigate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getTimeAgo } = useTimeAgo();
  const { isAuthenticated, isAuthLoading } = useSuperApp();
  const { items, isLoading, isError, markAllSeen, markRead } = useNotifications();

  // Opening the page is what clears the bell. Each row keeps its own unread tint until it
  // is actually opened, so the list still shows what is new after the dot goes out.
  useEffect(() => {
    markAllSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groups = useMemo(() => {
    const byBucket = {};
    items.forEach((item) => {
      const bucket = item.approx ? "today" : bucketOf(item.at);
      (byBucket[bucket] = byBucket[bucket] || []).push(item);
    });
    return BUCKET_ORDER.filter((b) => byBucket[b]?.length).map((b) => [b, byBucket[b]]);
  }, [items]);

  /**
   * The sentence for one row.
   *
   * An employer's mark arrives as the stored `complete_type` ("hired"); getApplicantStatus
   * turns it back into the label they actually pressed, so the reader sees "Тэнцсэн" and
   * never a database word.
   */
  const textFor = (item) => {
    const p = item.params || {};
    const statusLabel = getApplicantStatus(p.status)?.label || p.status || "";

    switch (item.kind) {
      case "stage":
        return {
          title: t("notifications.stageChanged", "Таны анкет дараагийн шатанд шилжлээ"),
          body: [p.company, p.job, p.stage].filter(Boolean).join(" · "),
        };
      case "status":
        return {
          title: t("notifications.statusChanged", "Ажил олгогч таны анкетыг тэмдэглэлээ"),
          body: [p.company, p.job, statusLabel].filter(Boolean).join(" · "),
        };
      case "cvView":
        return {
          title: t("notifications.cvViewed", "{{company}} таны CV-г үзлээ", { company: p.company }),
          body: "",
        };
      case "follow":
        return {
          title: t("notifications.followed", "{{company}} таны профайлыг үзлээ", { company: p.company }),
          body: "",
        };
      case "comment":
        return {
          title: t("notifications.newComment", "{{author}} таны пост дор сэтгэгдэл үлдээлээ", { author: p.author }),
          body: p.text || "",
        };
      case "reply":
        return {
          title: t("notifications.newReply", "{{author}} таны сэтгэгдэлд хариулав", { author: p.author }),
          body: p.text || "",
        };
      case "like":
        return {
          title: t("notifications.newLike", "{{author}} таны постод лайк дарлаа", { author: p.author }),
          body: p.text || "",
        };
      case "suggest":
        return {
          // `n` rather than `count`: i18next treats `count` as a plural selector and would
          // look for newSuggestions_one/_other, which these files deliberately do not carry.
          title: t("notifications.newSuggestions", "Таны зар дор {{n}} шинэ ажил санал болгож байна", { n: p.count }),
          body: "",
        };
      case "jobOffer":
        return {
          title: t("notifications.jobOffer", "{{company}} танд ажил санал болголоо", { company: p.company }),
          body: "",
        };
      case "applicant":
        return {
          title: t("notifications.newApplicant", "{{name}} анкетаа илгээлээ", { name: p.name }),
          body: p.job || "",
        };
      case "applicantStatus":
        return {
          title: t("notifications.applicantStatusChanged", "{{name}}-ийн төлөв өөрчлөгдлөө", { name: p.name }),
          body: [p.job, statusLabel].filter(Boolean).join(" · "),
        };
      default:
        return { title: "", body: "" };
    }
  };

  const open = (item) => {
    markRead(item.id);
    if (item.to) {
      onNavigate?.();
      navigate(item.to);
    }
  };

  const Row = ({ item }) => {
    const Icon = ICONS[item.kind] || FiBell;
    const { title, body } = textFor(item);

    return (
      <button
        type="button"
        onClick={() => open(item)}
        className={`w-full flex items-start gap-3 px-4 py-3.5 text-left border-b border-[#F1F5F9] last:border-b-0 transition-colors ${
          item.unread ? "bg-[#FFFBEB]" : "bg-white hover:bg-[#F8FAFC]"
        }`}
      >
        <span className="relative flex-none">
          {item.photoUrl ? (
            <img src={item.photoUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <span
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: TONES[item.kind] || "#F1F5F9" }}
            >
              <Icon className="text-[16px] text-[#475569]" />
            </span>
          )}
          {item.unread && (
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#EF4444] border-2 border-white" />
          )}
        </span>

        <span className="flex-1 min-w-0">
          <span className={`block text-[13.5px] leading-snug ${item.unread ? "font-extrabold text-[#020618]" : "font-bold text-[#334155]"}`}>
            {title}
          </span>
          {body && (
            <span className="block text-[12.5px] text-[#64748B] mt-0.5 line-clamp-2 break-words">{body}</span>
          )}
          <span className="block text-[11.5px] text-[#94A3B8] mt-1">
            {/* An event whose exact moment the server does not record is shown without one,
                rather than with a made-up "2 minutes ago". */}
            {item.approx ? t("notifications.recently", "саяхан") : getTimeAgo(item.at)}
          </span>
        </span>
      </button>
    );
  };


  if (!isAuthenticated && !isAuthLoading) {
    return (
      <p className="text-center text-[13px] font-medium text-[#94A3B8] py-16">
        {t("profile.loginToAccess", "Нэвтэрч орно уу")}
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="rounded-[18px] border border-[#E2E8F0] overflow-hidden bg-white">
        {[0, 1, 2, 3].map((i) => <RowSkeleton key={i} />)}
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="text-center py-16">
        <FiBell className="text-[28px] text-[#CBD5E1] mx-auto" />
        <p className="text-[13px] font-medium text-[#94A3B8] mt-3">
          {isError
            ? t("notifications.error", "Мэдэгдэл татахад алдаа гарлаа")
            : t("notifications.empty", "Одоогоор мэдэгдэл алга")}
        </p>
      </div>
    );
  }

  return groups.map(([bucket, rows]) => (
    <section key={bucket} className="mb-5 last:mb-0">
      <h2 className="text-[12px] font-extrabold text-[#94A3B8] uppercase tracking-wide mb-2 px-1">
        {t(`notifications.${bucket}`, {
          today: "Өнөөдөр",
          yesterday: "Өчигдөр",
          thisWeek: "Энэ долоо хоног",
          earlier: "Эрт",
        }[bucket])}
      </h2>
      <div className="rounded-[18px] border border-[#E2E8F0] overflow-hidden bg-white">
        {rows.map((item) => <Row key={item.id} item={item} />)}
      </div>
    </section>
  ));
};

export default NotificationList;
